import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  Button,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Paper,
  CircularProgress,
  Switch,
  FormControlLabel,
  Snackbar,
  Autocomplete,
  Checkbox,
  ListItemText,
  Breadcrumbs,
  Link
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  Refresh as RefreshIcon,
  ManageAccounts as ManageIcon,
  LockReset as ResetPasswordIcon,
  VerifiedUser as VerifiedIcon,
  Cancel as CancelIcon,
  LocationOn as LocationIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import api from '../services/auth';
import { getCurrentUser } from '../services/auth';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const ManageUser = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [dialogMode, setDialogMode] = useState('add');
  const [entreprises, setEntreprises] = useState([]);
  const [succursales, setSuccursales] = useState([]);
  const [filteredSuccursales, setFilteredSuccursales] = useState([]);
  const [loadingSuccursales, setLoadingSuccursales] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [selectedEntreprise, setSelectedEntreprise] = useState(null);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  // Nouvel état pour le filtre par succursale
  const [succursaleFilter, setSuccursaleFilter] = useState('');
  const [allSuccursales, setAllSuccursales] = useState([]);
  const [showSuccursaleFilter, setShowSuccursaleFilter] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    password2: '',
    email: '',
    first_name: '',
    last_name: '',
    role: 'vendeur',
    entreprise: '',
    succursale_principale: '',
    succursales_ids: [],
    telephone: ''
  });

  const [formErrors, setFormErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  // Rôles disponibles selon l'utilisateur connecté
  const getAvailableRoles = () => {
    if (!currentUser) return [];
    
    const roles = [
      { value: 'vendeur', label: 'Vendeur' },
      { value: 'caissier', label: 'Caissier' },
      { value: 'manager', label: 'Manager' }
    ];
    
    if (currentUser.role === 'super_admin') {
      roles.push(
        { value: 'proprietaire', label: 'Propriétaire' },
        { value: 'super_admin', label: 'Super Admin' }
      );
    } else if (currentUser.role === 'proprietaire') {
      // roles.push(
      //   { value: 'proprietaire', label: 'Propriétaire' }
      // );
    }
    
    return roles;
  };

  // 1. Premier useEffect - Récupérer l'utilisateur connecté
  useEffect(() => {
    const loadCurrentUser = async () => {
      const user = getCurrentUser();
      setCurrentUser(user);
      
      if (!user) {
        window.location.href = '/login';
        return;
      }
      
      setInitialLoadDone(true);
    };
    
    loadCurrentUser();
  }, []);

  // 2. Deuxième useEffect - Charger les données quand currentUser est disponible
  useEffect(() => {
    if (currentUser && initialLoadDone) {
      fetchUsers();
      fetchAllSuccursales(); // Charger toutes les succursales pour le filtre
      // fetchEntreprises(); // Décommentez si nécessaire
    }
  }, [currentUser, initialLoadDone]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const fetchAllSuccursales = async () => {
    try {
      const response = await api.get('/succursales/');
      
      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data?.results) {
        data = response.data.results;
      } else if (response.data?.data) {
        data = response.data.data;
      }
      
      // Filtrer selon les permissions si nécessaire
      let filteredData = data;
      if (currentUser?.role === 'manager') {
        // Pour un manager, ne montrer que sa succursale
        filteredData = data.filter(s => s.id === currentUser.succursale_principale);
      } else if (currentUser?.role === 'proprietaire') {
        // Pour un propriétaire, montrer les succursales de son entreprise
        filteredData = data.filter(s => {
          const succursaleEntrepriseId = s.entreprise?.id || s.entreprise;
          return succursaleEntrepriseId == currentUser.entreprise;
        });
      }
      
      setAllSuccursales(filteredData);
    } catch (err) {
      console.error('Erreur chargement succursales:', err);
      setAllSuccursales([]);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('users/');
      
      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data?.results) {
        data = response.data.results;
      } else if (response.data?.data) {
        data = response.data.data;
      }
      
      // Filtrer selon les permissions (maintenant currentUser est défini)
      const filteredUsers = data.filter(user => {
        if (!currentUser) return false;
        
        if (currentUser.role === 'super_admin') return true;
        if (currentUser.role === 'proprietaire') {
          return user.entreprise === currentUser.entreprise;
        }
        if (currentUser.role === 'manager') {
          // Managers voient les utilisateurs de leurs succursales
          return user.succursale_principale === currentUser.succursale_principale ||
                 user.succursales?.includes(currentUser.succursale_principale);
        }
        return false;
      });
      
      setUsers(filteredUsers);
      setError('');
    } catch (err) {
      console.error('Erreur utilisateurs:', err);
      let errorMsg = 'Erreur lors du chargement des utilisateurs';
      
      if (err.response?.status === 401) {
        errorMsg = 'Session expirée, veuillez vous reconnecter';
      } else if (err.response?.status === 403) {
        errorMsg = 'Permission refusée';
      } else if (err.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      }
      
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // const fetchEntreprises = async () => {
  //   try {
  //     const response = await api.get('/entreprises/');
  //     
  //     let data = [];
  //     if (Array.isArray(response.data)) {
  //       data = response.data;
  //     } else if (response.data?.results) {
  //       data = response.data.results;
  //     } else if (response.data?.data) {
  //       data = response.data.data;
  //     }
  //     
  //     // Filtrer selon les permissions
  //     let filteredEntreprises = data;
  //     if (currentUser?.role === 'proprietaire') {
  //       filteredEntreprises = data.filter(e => e.id === currentUser.entreprise);
  //     } else if (currentUser?.role === 'manager') {
  //       filteredEntreprises = data.filter(e => e.id === currentUser.entreprise);
  //     }
  //     
  //     setEntreprises(filteredEntreprises);
  //     
  //     // Pré-sélectionner l'entreprise pour les non-super_admins
  //     if (currentUser && currentUser.role !== 'super_admin' && currentUser.entreprise) {
  //       setFormData(prev => ({
  //         ...prev,
  //         entreprise: currentUser.entreprise
  //       }));
  //       
  //       // Trouver l'entreprise sélectionnée
  //       const entreprise = filteredEntreprises.find(e => e.id === currentUser.entreprise);
  //       setSelectedEntreprise(entreprise);
  //       
  //       // Charger les succursales de cette entreprise
  //       fetchSuccursalesByEntreprise(currentUser.entreprise);
  //     }
  //   } catch (err) {
  //     console.error('Erreur entreprises:', err);
  //     setEntreprises([]);
  //   }
  // };

  const fetchSuccursalesByEntreprise = async (entrepriseId) => {
    if (!entrepriseId) {
      setFilteredSuccursales([]);
      setSuccursales([]);
      return;
    }
    
    try {
      setLoadingSuccursales(true);
      console.log('Récupération succursales pour entreprise:', entrepriseId);
      
      let response;
      
      if (currentUser?.role === 'super_admin') {
        // Super admin peut filtrer par entreprise
        response = await api.get('/succursales/', {
          params: { entreprise: entrepriseId }
        });
      } else {
        // Autres rôles - récupérer toutes les succursales puis filtrer côté client
        response = await api.get('/succursales/');
      }
      
      let allSuccursales = [];
      if (Array.isArray(response.data)) {
        allSuccursales = response.data;
      } else if (response.data?.results) {
        allSuccursales = response.data.results;
      } else if (response.data?.data) {
        allSuccursales = response.data.data;
      }
      
      setSuccursales(allSuccursales);
      
      // Filtrer les succursales par entreprise
      const filtered = allSuccursales.filter(s => {
        // Vérifier selon la structure de vos données
        const succursaleEntrepriseId = s.entreprise?.id || s.entreprise;
        return succursaleEntrepriseId == entrepriseId; // == au lieu de === pour gérer string/number
      });
      
      console.log('Succursales totales:', allSuccursales.length);
      console.log('Succursales filtrées:', filtered.length);
      console.log('Filtre appliqué pour entreprise ID:', entrepriseId);
      
      setFilteredSuccursales(filtered);
      
      // Si on a déjà une succursale principale qui n'appartient pas à cette entreprise, la réinitialiser
      if (formData.succursale_principale && filtered.length > 0) {
        const currentSuccursaleId = parseInt(formData.succursale_principale);
        if (!filtered.some(s => s.id === currentSuccursaleId)) {
          console.log('Réinitialisation succursale principale car non trouvée dans filtered');
          setFormData(prev => ({
            ...prev,
            succursale_principale: '',
            succursales_ids: []
          }));
        }
      }
      
      // Si aucune succursale trouvée mais entreprise existe
      if (filtered.length === 0 && selectedEntreprise) {
        console.warn('Aucune succursale trouvée pour cette entreprise');
        showSnackbar(`Aucune succursale trouvée pour ${selectedEntreprise.nom}`, 'warning');
      }
      
    } catch (err) {
      console.error('Erreur succursales:', err.response?.data || err);
      let errorMsg = 'Erreur lors du chargement des succursales';
      
      if (err.response?.status === 403) {
        errorMsg = 'Permission refusée pour accéder aux succursales';
      } else if (err.response?.status === 404) {
        errorMsg = 'Succursales non trouvées';
      }
      
      setSuccursales([]);
      setFilteredSuccursales([]);
      showSnackbar(errorMsg, 'error');
    } finally {
      setLoadingSuccursales(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const handleSuccursaleFilterChange = (event) => {
    setSuccursaleFilter(event.target.value);
    setPage(0);
  };

  const toggleSuccursaleFilter = () => {
    setShowSuccursaleFilter(!showSuccursaleFilter);
    if (showSuccursaleFilter) {
      setSuccursaleFilter(''); // Réinitialiser le filtre quand on cache
    }
  };

  const clearFilters = () => {
    setSearch('');
    setSuccursaleFilter('');
  };

  const handleOpenDialog = (mode, user = null) => {
    setDialogMode(mode);
    setSelectedUser(user);
    setFormErrors({});
    setSelectedEntreprise(null);
    
    if (mode === 'edit' || mode === 'view') {
      const entrepriseId = user.entreprise?.id || user.entreprise;
      const entreprise = entreprises.find(e => e.id === entrepriseId);
      setSelectedEntreprise(entreprise);
      
      setFormData({
        username: user.username || '',
        password: '',
        password2: '',
        email: user.email || '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        role: user.role || 'vendeur',
        entreprise: entrepriseId || '',
        succursale_principale: user.succursale_principale?.id || user.succursale_principale || '',
        succursales_ids: user.succursales?.map(s => s.id) || user.succursales || [],
        telephone: user.telephone || ''
      });
      
      // Charger les succursales si entreprise définie
      if (entrepriseId) {
        fetchSuccursalesByEntreprise(entrepriseId);
      }
    } else {
      // Reset form for add mode
      const newFormData = {
        username: '',
        password: '',
        password2: '',
        email: '',
        first_name: '',
        last_name: '',
        role: 'vendeur',
        entreprise: '',
        succursale_principale: '',
        succursales_ids: [],
        telephone: ''
      };
      
      // Pré-sélectionner l'entreprise pour les non-super_admins
      if (currentUser && currentUser.role !== 'super_admin' && currentUser.entreprise) {
        const entreprise = entreprises.find(e => e.id === currentUser.entreprise);
        setSelectedEntreprise(entreprise);
        newFormData.entreprise = currentUser.entreprise;
        
        // Charger les succursales
        fetchSuccursalesByEntreprise(currentUser.entreprise);
      }
      
      setFormData(newFormData);
      setFilteredSuccursales([]);
    }
    
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
    setFormErrors({});
    setShowPassword(false);
    setSelectedEntreprise(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'entreprise') {
      // Trouver l'entreprise sélectionnée
      const entreprise = entreprises.find(e => e.id === value);
      setSelectedEntreprise(entreprise);
      
      // Charger les succursales de cette entreprise
      fetchSuccursalesByEntreprise(value);
      
      // Réinitialiser les succursales sélectionnées
      setFormData(prev => ({
        ...prev,
        [name]: value,
        succursale_principale: '',
        succursales_ids: []
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Effacer l'erreur pour ce champ
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSuccursalesChange = (event) => {
    const { value } = event.target;
    setFormData(prev => ({
      ...prev,
      succursales_ids: value
    }));
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.username?.trim()) {
      errors.username = 'Nom d\'utilisateur requis';
    }
    
    if (dialogMode === 'add' && !formData.password) {
      errors.password = 'Mot de passe requis';
    }
    
    if (dialogMode === 'add' && formData.password && formData.password.length < 6) {
      errors.password = 'Le mot de passe doit contenir au moins 6 caractères';
    }
    
    if (dialogMode === 'add' && formData.password !== formData.password2) {
      errors.password2 = 'Les mots de passe ne correspondent pas';
    }
    
    if (!formData.role) {
      errors.role = 'Rôle requis';
    }
    
    if (!formData.entreprise) {
      errors.entreprise = 'Entreprise requise';
    }
    
    // VALIDATION OBLIGATOIRE DE LA SUCCURSALE
    if (!formData.succursale_principale) {
      errors.succursale_principale = 'Succursale principale obligatoire';
    }
    
    // Validation du rôle selon les permissions
    if (formData.role === 'super_admin' && currentUser?.role !== 'super_admin') {
      errors.role = 'Seul un Super Admin peut créer un Super Admin';
    }
    
    if (formData.role === 'proprietaire' && currentUser?.role !== 'super_admin') {
      errors.role = 'Seul un Super Admin peut créer un Propriétaire';
    }
    
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Email invalide';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      showSnackbar('Veuillez corriger les erreurs dans le formulaire', 'error');
      return;
    }
    
    setActionLoading(true);
    
    try {
      const dataToSend = { 
        ...formData,
        // S'assurer que les IDs sont des nombres
        entreprise: parseInt(formData.entreprise),
        succursale_principale: parseInt(formData.succursale_principale),
        succursales_ids: formData.succursales_ids.map(id => parseInt(id))
      };
      
      // Pour l'édition, ne pas envoyer les mots de passe s'ils sont vides
      if (dialogMode === 'edit' && !dataToSend.password) {
        delete dataToSend.password;
        delete dataToSend.password2;
      }
      
      console.log('Données envoyées:', dataToSend);
      
      if (dialogMode === 'add') {
        const response = await api.post('/users/', dataToSend);
        console.log('Utilisateur créé:', response.data);
        showSnackbar('Utilisateur créé avec succès', 'success');
      } else if (dialogMode === 'edit') {
        const response = await api.put(`/users/${selectedUser.id}/`, dataToSend);
        console.log('Utilisateur modifié:', response.data);
        showSnackbar('Utilisateur modifié avec succès', 'success');
      }
      
      await fetchUsers();
      handleCloseDialog();
      
    } catch (err) {
      console.error('Erreur enregistrement:', err.response?.data || err);
      
      if (err.response?.status === 400) {
        const apiErrors = err.response.data;
        console.error('Erreurs de validation:', apiErrors);
        
        // Traitement spécial pour les erreurs de succursale
        if (apiErrors.succursale_principale) {
          setFormErrors(prev => ({
            ...prev,
            succursale_principale: Array.isArray(apiErrors.succursale_principale) 
              ? apiErrors.succursale_principale.join(', ') 
              : apiErrors.succursale_principale
          }));
        }
        
        // Autres erreurs
        const otherErrors = { ...apiErrors };
        delete otherErrors.succursale_principale;
        setFormErrors(otherErrors);
        
        showSnackbar('Erreurs de validation, vérifiez le formulaire', 'error');
      } else if (err.response?.status === 403) {
        showSnackbar('Permission refusée', 'error');
      } else if (err.response?.status === 401) {
        showSnackbar('Session expirée', 'error');
      } else {
        showSnackbar('Erreur lors de l\'enregistrement', 'error');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) return;
    
    setActionLoading(true);
    
    try {
      await api.delete(`/users/${id}/`);
      showSnackbar('Utilisateur supprimé avec succès', 'success');
      await fetchUsers();
      
    } catch (err) {
      console.error('Erreur suppression:', err);
      showSnackbar('Erreur lors de la suppression', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetPassword = async (id) => {
    if (!window.confirm('Réinitialiser le mot de passe de cet utilisateur ?')) return;
    
    setActionLoading(true);
    
    try {
      await api.post(`/users/${id}/reset-password/`);
      showSnackbar('Mot de passe réinitialisé avec succès', 'success');
      
    } catch (err) {
      console.error('Erreur réinitialisation:', err);
      showSnackbar('Erreur lors de la réinitialisation', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleActive = async (id, isActive) => {
    setActionLoading(true);
    
    try {
      await api.patch(`/users/${id}/`, {
        is_active: !isActive
      });
      
      await fetchUsers();
      showSnackbar(`Utilisateur ${!isActive ? 'activé' : 'désactivé'} avec succès`, 'success');
      
    } catch (err) {
      console.error('Erreur changement statut:', err);
      showSnackbar('Erreur lors du changement de statut', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchUsers();
    fetchAllSuccursales();
    showSnackbar('Données actualisées', 'info');
  };

  const canCreateUser = () => {
    if (!currentUser) return false;
    return currentUser.role === 'super_admin' || 
           currentUser.role === 'proprietaire' || 
           currentUser.role === 'manager';
  };

  const canEditUser = (user) => {
    if (!currentUser) return false;
    if (currentUser.role === 'super_admin') return true;
    if (currentUser.role === 'proprietaire' && user.entreprise === currentUser.entreprise) return true;
    if (currentUser.role === 'manager' && user.entreprise === currentUser.entreprise) return true;
    return false;
  };

  const canDeleteUser = (user) => {
    if (!currentUser) return false;
    if (user.role === 'super_admin') return false;
    if (currentUser.role === 'super_admin') return true;
    if (currentUser.role === 'proprietaire' && user.entreprise === currentUser.entreprise && user.role !== 'super_admin') return true;
    return false;
  };

  const safeUsers = Array.isArray(users) ? users : [];

  // Filtrer les utilisateurs par recherche ET par succursale
  const filteredUsers = safeUsers.filter(user => {
    if (!user) return false;
    
    // Filtre par recherche textuelle
    const searchLower = search.toLowerCase();
    const matchesSearch = (
      (user.username?.toLowerCase() || '').includes(searchLower) ||
      (user.email?.toLowerCase() || '').includes(searchLower) ||
      (user.first_name?.toLowerCase() || '').includes(searchLower) ||
      (user.last_name?.toLowerCase() || '').includes(searchLower) ||
      (user.role?.toLowerCase() || '').includes(searchLower) ||
      (user.telephone?.toLowerCase() || '').includes(searchLower)
    );
    
    if (!matchesSearch) return false;
    
    // Filtre par succursale (si un filtre est sélectionné)
    if (succursaleFilter) {
      const userSuccursaleId = user.succursale_principale?.id || user.succursale_principale;
      const userSuccursaleIds = user.succursales?.map(s => s.id) || user.succursales || [];
      
      // Vérifier si l'utilisateur est rattaché à la succursale filtrée
      return userSuccursaleId == succursaleFilter || userSuccursaleIds.includes(parseInt(succursaleFilter));
    }
    
    return true;
  });

  const paginatedUsers = filteredUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(parseISO(dateString), 'dd/MM/yyyy', { locale: fr });
    } catch (e) {
      return dateString;
    }
  };

  const getRoleLabel = (role) => {
    const roleLabels = {
      'super_admin': 'Super Admin',
      'proprietaire': 'Propriétaire',
      'manager': 'Manager',
      'vendeur': 'Vendeur',
      'caissier': 'Caissier'
    };
    return roleLabels[role] || role;
  };

  const getRoleColor = (role) => {
    const roleColors = {
      'super_admin': 'error',
      'proprietaire': 'warning',
      'manager': 'info',
      'vendeur': 'success',
      'caissier': 'primary'
    };
    return roleColors[role] || 'default';
  };

  const getSuccursaleName = (id) => {
    const succursale = allSuccursales.find(s => s.id === id);
    return succursale ? succursale.nom : 'N/A';
  };

  // Afficher un loader pendant le chargement initial
  if (!initialLoadDone || (loading && !currentUser)) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Chargement...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumb */}
      <Breadcrumbs 
        separator={<NavigateNextIcon fontSize="small" />} 
        sx={{ mb: 3 }}
      >
        <Link href="/dashboard" underline="hover" color="inherit">
          <HomeIcon sx={{ mr: 0.5, fontSize: 20 }} />
          Dashboard
        </Link>
        <Typography color="text.primary">
          <ManageIcon sx={{ mr: 0.5, fontSize: 20, verticalAlign: 'bottom' }} />
          Gestion des Utilisateurs
        </Typography>
      </Breadcrumbs>

      <Card sx={{ p: 3, mb: 3, boxShadow: 3, borderRadius: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Gestion des Utilisateurs
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {filteredUsers.length} utilisateur(s) trouvé(s)
              {succursaleFilter && ` • Filtré par: ${getSuccursaleName(succursaleFilter)}`}
            </Typography>
          </Box>
          
          <Box display="flex" gap={2}>
            <Tooltip title="Filtrer par succursale">
              <Button
                variant={showSuccursaleFilter ? "contained" : "outlined"}
                startIcon={<FilterIcon />}
                onClick={toggleSuccursaleFilter}
                color={showSuccursaleFilter ? "primary" : "inherit"}
                sx={{ borderRadius: '8px' }}
              >
                {showSuccursaleFilter ? "Masquer filtre" : "Filtrer"}
              </Button>
            </Tooltip>
            
            <Tooltip title="Actualiser">
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={actionLoading || loading}
                sx={{ borderRadius: '8px' }}
              >
                Actualiser
              </Button>
            </Tooltip>
            
            {canCreateUser() && (
              <Tooltip title="Ajouter un nouvel utilisateur">
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog('add')}
                  disabled={actionLoading}
                  sx={{ borderRadius: '8px' }}
                >
                  Nouvel Utilisateur
                </Button>
              </Tooltip>
            )}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Zone de recherche avec filtre intégré */}
        <Box mb={3}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={showSuccursaleFilter ? 8 : 12}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Rechercher par nom, email, prénom, rôle, téléphone..."
                value={search}
                onChange={handleSearch}
                disabled={actionLoading}
                sx={{ 
                  borderRadius: '8px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px'
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (search || succursaleFilter) && (
                    <InputAdornment position="end">
                      <Tooltip title="Effacer les filtres">
                        <IconButton 
                          size="small" 
                          onClick={clearFilters}
                          disabled={actionLoading}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            
            {/* Filtre par succursale (affiché conditionnellement) */}
            {showSuccursaleFilter && (
              <Grid item xs={4}>
                <FormControl fullWidth size="medium">
                  <InputLabel>Succursale</InputLabel>
                  <Select
                    value={succursaleFilter}
                    onChange={handleSuccursaleFilterChange}
                    label="Succursale"
                  >
                    <MenuItem value="">Toutes les succursales</MenuItem>
                    {allSuccursales.map((succursale) => (
                      <MenuItem key={succursale.id} value={succursale.id}>
                        <Box display="flex" alignItems="center">
                          <LocationIcon sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />
                          {succursale.nom}
                          {succursale.is_main_branch && (
                            <Chip 
                              label="Principale" 
                              size="small" 
                              sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </Box>

        <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 2 }}>
          <Table>
            <TableHead sx={{ bgcolor: 'primary.main' }}>
              <TableRow>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Utilisateur</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Email/Téléphone</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Rôle</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Succursale</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Créé le</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Statut</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Box display="flex" justifyContent="center" alignItems="center">
                      <CircularProgress size={24} sx={{ mr: 2 }} />
                      <Typography color="text.secondary">
                        Chargement des utilisateurs...
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : paginatedUsers.length > 0 ? (
                paginatedUsers.map((user) => (
                  <TableRow 
                    key={user.id}
                    hover
                    sx={{ 
                      '&:hover': { bgcolor: 'action.hover' },
                      opacity: user.is_active !== false ? 1 : 0.7
                    }}
                  >
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {user.first_name && user.last_name 
                              ? `${user.first_name} ${user.last_name}`
                              : user.username}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            @{user.username}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" gutterBottom>
                        {user.email || 'N/A'}
                      </Typography>
                      {user.telephone && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          <PhoneIcon sx={{ fontSize: 12, mr: 0.5 }} />
                          {user.telephone}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getRoleLabel(user.role)}
                        color={getRoleColor(user.role)}
                        size="small"
                        sx={{ fontWeight: 'medium' }}
                      />
                    </TableCell>
                    <TableCell>
                      {user.succursale_principale_detail ? (
                        <Box>
                          <Typography variant="body2">
                            {user.succursale_principale_detail.nom}
                          </Typography>
                          {user.succursale_principale_detail.is_main_branch && (
                            <Chip 
                              label="Principale" 
                              size="small" 
                              sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                          {user.succursales_count > 1 && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              +{user.succursales_count - 1} autre(s)
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {getSuccursaleName(user.succursale_principale) || 'N/A'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(user.date_joined || user.date_creation)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={user.is_active !== false ? <VerifiedIcon /> : <CancelIcon />}
                        label={user.is_active !== false ? 'Actif' : 'Inactif'}
                        color={user.is_active !== false ? 'success' : 'error'}
                        size="small"
                        onClick={() => handleToggleActive(user.id, user.is_active)}
                        sx={{ 
                          cursor: canEditUser(user) ? 'pointer' : 'default',
                          fontWeight: 'medium'
                        }}
                        disabled={actionLoading || !canEditUser(user)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="Voir détails">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog('view', user)}
                            color="info"
                            disabled={actionLoading}
                            sx={{ 
                              bgcolor: 'primary.main', 
                              '&:hover': { bgcolor: 'info.main', color: 'white' }
                            }}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        {canEditUser(user) && (
                          <Tooltip title="Modifier">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog('edit', user)}
                              color="primary"
                              disabled={actionLoading}
                              sx={{ 
                                bgcolor: 'secondary.light', 
                                '&:hover': { bgcolor: 'primary.main', color: 'white' }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        
                        {canEditUser(user) && (
                          <Tooltip title="Réinitialiser mot de passe">
                            <IconButton
                              size="small"
                              onClick={() => handleResetPassword(user.id)}
                              color="warning"
                              disabled={actionLoading}
                              sx={{ 
                                bgcolor: 'primary.main', 
                                '&:hover': { bgcolor: 'warning.main', color: 'white' }
                              }}
                            >
                              <ResetPasswordIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        
                        {canDeleteUser(user) && (
                          <Tooltip title="Supprimer">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(user.id)}
                              color="error"
                              disabled={actionLoading}
                              sx={{ 
                                bgcolor: 'secondary.light', 
                                '&:hover': { bgcolor: 'error.main', color: 'white' }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {search || succursaleFilter 
                        ? 'Aucun utilisateur ne correspond à vos critères de recherche'
                        : 'Aucun utilisateur trouvé'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          
          {filteredUsers.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredUsers.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Lignes par page:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} sur ${count}`
              }
            />
          )}
        </Paper>
      </Card>

      {/* Dialog pour ajouter/modifier/voir */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'primary.main', 
          color: 'white',
          borderTopLeftRadius: 2,
          borderTopRightRadius: 2
        }}>
          <Box display="flex" alignItems="center">
            {dialogMode === 'add' ? <AddIcon sx={{ mr: 1 }} /> : 
             dialogMode === 'edit' ? <EditIcon sx={{ mr: 1 }} /> : 
             <ViewIcon sx={{ mr: 1 }} />}
            {dialogMode === 'add' ? 'Nouvel Utilisateur' : 
             dialogMode === 'edit' ? 'Modifier l\'Utilisateur' : 
             'Détails de l\'Utilisateur'}
          </Box>
          
          {selectedUser?.username && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.9 }}>
              @{selectedUser.username}
            </Typography>
          )}
        </DialogTitle>
        
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            {/* Informations Entreprise */}
            {selectedEntreprise && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Entreprise sélectionnée:
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        <BusinessIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'text-bottom' }} />
                        {selectedEntreprise.nom}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Module: {selectedEntreprise.module_actif || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Succursales disponibles:
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {filteredSuccursales.length} succursale(s)
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            )}
            
            {/* Champs du formulaire */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nom d'utilisateur *"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                error={!!formErrors.username}
                helperText={formErrors.username}
                disabled={dialogMode === 'view' || actionLoading}
                sx={{ mt: 1 }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                error={!!formErrors.email}
                helperText={formErrors.email}
                disabled={dialogMode === 'view' || actionLoading}
                sx={{ mt: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            {dialogMode !== 'view' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Mot de passe *"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleInputChange}
                    error={!!formErrors.password}
                    helperText={formErrors.password || (dialogMode === 'edit' ? 'Laisser vide pour ne pas changer' : 'Min. 6 caractères')}
                    disabled={actionLoading}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Confirmer le mot de passe *"
                    name="password2"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password2}
                    onChange={handleInputChange}
                    error={!!formErrors.password2}
                    helperText={formErrors.password2}
                    disabled={actionLoading}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showPassword}
                        onChange={(e) => setShowPassword(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Afficher le mot de passe"
                  />
                </Grid>
              </>
            )}
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Prénom"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                disabled={dialogMode === 'view' || actionLoading}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nom"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                disabled={dialogMode === 'view' || actionLoading}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Téléphone"
                name="telephone"
                value={formData.telephone}
                onChange={handleInputChange}
                disabled={dialogMode === 'view' || actionLoading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl 
                fullWidth 
                error={!!formErrors.role}
                disabled={dialogMode === 'view' || actionLoading}
              >
                <InputLabel>Rôle *</InputLabel>
                <Select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  label="Rôle *"
                >
                  {getAvailableRoles().map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      {role.label}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.role && (
                  <Typography variant="caption" color="error">
                    {formErrors.role}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            
            {/* Entreprise (lecture seule pour non super_admin) */}
            <Grid item xs={12} sm={6}>
              <FormControl 
                fullWidth 
                error={!!formErrors.entreprise}
                disabled={dialogMode === 'view' || actionLoading || (currentUser && currentUser.role !== 'super_admin')}
              >
                <InputLabel>Entreprise *</InputLabel>
                <Select
                  name="entreprise"
                  value={formData.entreprise}
                  onChange={handleInputChange}
                  label="Entreprise *"
                >
                  {entreprises.map((entreprise) => (
                    <MenuItem key={entreprise.id} value={entreprise.id}>
                      <Box>
                        <Typography variant="body1">
                          {entreprise.nom}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {entreprise.module_actif} • {entreprise.succursales?.length || 0} succ.
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.entreprise && (
                  <Typography variant="caption" color="error">
                    {formErrors.entreprise}
                  </Typography>
                )}
                {currentUser && currentUser.role !== 'super_admin' && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  
                  </Typography>
                )}
              </FormControl>
            </Grid>
            
            {/* SUCCURSALE PRINCIPALE OBLIGATOIRE */}
            <Grid item xs={12} sm={6}>
              <FormControl 
                fullWidth 
                error={!!formErrors.succursale_principale}
                disabled={dialogMode === 'view' || actionLoading || loadingSuccursales || !formData.entreprise}
              >
                <InputLabel>Succursale principale *</InputLabel>
                <Select
                  name="succursale_principale"
                  value={formData.succursale_principale}
                  onChange={handleInputChange}
                  label="Succursale principale *"
                >
                  {loadingSuccursales ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Chargement des succursales...
                    </MenuItem>
                  ) : filteredSuccursales.length === 0 ? (
                    <MenuItem disabled>
                      Aucune succursale disponible pour cette entreprise
                    </MenuItem>
                  ) : (
                    filteredSuccursales.map((succursale) => (
                      <MenuItem key={succursale.id} value={succursale.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          <Box>
                            <Typography variant="body1">
                              {succursale.nom}
                              {succursale.is_main_branch && (
                                <Chip label="Principale" size="small" sx={{ ml: 1 }} />
                              )}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {succursale.code || 'N/A'} • {succursale.ville || 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    ))
                  )}
                </Select>
                {formErrors.succursale_principale ? (
                  <Typography variant="caption" color="error">
                    {formErrors.succursale_principale}
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {!formData.entreprise ? 'Sélectionnez d\'abord une entreprise' : 
                     loadingSuccursales ? 'Chargement...' :
                     'Succursale principale obligatoire'}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            
            {/* Succursales d'accès supplémentaires */}
            <Grid item xs={12}>
              <FormControl 
                fullWidth 
                disabled={dialogMode === 'view' || actionLoading || loadingSuccursales || !formData.entreprise || !formData.succursale_principale}
              >
                <InputLabel>Succursales supplémentaires d'accès</InputLabel>
                <Select
                  multiple
                  name="succursales_ids"
                  value={formData.succursales_ids}
                  onChange={handleSuccursalesChange}
                  label="Succursales supplémentaires d'accès"
                  renderValue={(selected) => {
                    const selectedSuccursales = filteredSuccursales.filter(s => selected.includes(s.id));
                    return selectedSuccursales.map(s => s.nom).join(', ') || 'Aucune';
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300
                      }
                    }
                  }}
                >
                  {filteredSuccursales
                    .filter(s => s.id !== formData.succursale_principale) // Exclure la succursale principale
                    .map((succursale) => (
                      <MenuItem key={succursale.id} value={succursale.id}>
                        <Checkbox checked={formData.succursales_ids.indexOf(succursale.id) > -1} />
                        <ListItemText 
                          primary={succursale.nom} 
                          secondary={`${succursale.code || 'N/A'} - ${succursale.ville}`}
                        />
                      </MenuItem>
                    ))
                  }
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Sélectionnez les succursales supplémentaires auxquelles l'utilisateur aura accès (optionnel)
                </Typography>
              </FormControl>
            </Grid>
            
            {/* Affichage des erreurs */}
            {Object.keys(formErrors).filter(key => !['username', 'email', 'password', 'password2', 'role', 'entreprise', 'succursale_principale'].includes(key)).length > 0 && (
              <Grid item xs={12}>
                <Alert severity="error" sx={{ mt: 2 }}>
                  <Typography variant="subtitle2">Erreurs de validation :</Typography>
                  <ul style={{ margin: 0, paddingLeft: '20px' }}>
                    {Object.entries(formErrors)
                      .filter(([key]) => !['username', 'email', 'password', 'password2', 'role', 'entreprise', 'succursale_principale'].includes(key))
                      .map(([key, value]) => (
                        <li key={key}>
                          <strong>{key}</strong>: {Array.isArray(value) ? value.join(', ') : value}
                        </li>
                      ))
                    }
                  </ul>
                </Alert>
              </Grid>
            )}
            
            {/* Informations de l'utilisateur existant */}
            {/* {dialogMode === 'view' && selectedUser && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: 'primary.light.50' }}>
                  <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                    Informations supplémentaires:
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Créé le:</strong> {formatDate(selectedUser.date_joined || selectedUser.date_creation)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Dernière connexion:</strong> {formatDate(selectedUser.last_login) || 'Jamais'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2">
                        <strong>Statut:</strong> {selectedUser.is_active !== false ? 'Actif' : 'Inactif'} • 
                        {selectedUser.is_staff ? ' Staff' : ''} • 
                        {selectedUser.is_superuser ? ' Superuser' : ''}
                      </Typography>
                    </Grid>
                    {selectedUser.succursales_count > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="body2">
                          <strong>Succursales accessibles:</strong> {selectedUser.succursales_count}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </Grid>
            )} */}
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
          <Button 
            onClick={handleCloseDialog} 
            disabled={actionLoading}
            variant="outlined"
            sx={{ borderRadius: '8px' }}
          >
            {dialogMode === 'view' ? 'Fermer' : 'Annuler'}
          </Button>
          
          {dialogMode !== 'view' && (
            <Button 
              onClick={handleSubmit} 
              variant="contained"
              disabled={actionLoading}
              startIcon={actionLoading ? <CircularProgress size={20} /> : null}
              sx={{ borderRadius: '8px' }}
            >
              {dialogMode === 'add' ? 'Créer l\'utilisateur' : 'Enregistrer les modifications'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar pour les notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbarSeverity} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageUser;